import { BLOCK_TYPES, getLiquidReferencedVariables } from "../../../src/shared/schemas/PluginSchema";

const DOCUMENT_VARIABLES = ["preceding_text", "highlighted_text", "previous_document_text", "documentId"] as const;

export type DocumentVariable = (typeof DOCUMENT_VARIABLES)[number];

const isDocumentVariable = (value: string): value is DocumentVariable => {
  return (DOCUMENT_VARIABLES as readonly string[]).includes(value);
};

export interface BlockOptionOutput {
  source: string;
  target?: string;
  minWords?: number;
  maxWords?: number;
  [key: string]: unknown;
}

export interface BlockOutput {
  name: string;
  type: string;
  [key: string]: unknown;
}

export interface BlockInput {
  name: string;
  type: string;
  [key: string]: unknown;
}

export interface PromptMessage {
  role: string;
  template?: string;
  [key: string]: unknown;
}

export interface PromptDefinition {
  messages?: PromptMessage[];
  [key: string]: unknown;
}

export interface PluginBlock {
  id: string;
  blockType: string;
  inputs?: BlockInput[];
  outputs?: BlockOutput[];
  options?: {
    outputs?: BlockOptionOutput[];
    prompt?: PromptDefinition;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface PluginConnection {
  source: string;
  sourcePath: string;
  target: string;
  targetPath: string;
  [key: string]: unknown;
}

export interface PluginDefinition {
  id: string;
  name: string;
  type: string;
  description?: string;
  status?: string;
  promptVisibility?: string;
  categoryId?: string | null;
  shortId?: string | null;
  blocks?: PluginBlock[];
  connections?: PluginConnection[];
  userId?: string;
  userEmail?: string | null;
  userDisplayName?: string | null;
  [key: string]: unknown;
}

export interface RepairChangeLog {
  addedOptionOutputs: DocumentVariable[];
  addedBlockOutputs: DocumentVariable[];
  addedConnections: Array<{ blockId: string; variable: DocumentVariable }>;
  addedInputs: Array<{ blockId: string; variable: DocumentVariable }>;
  reason?: string;
}

export interface RepairResult {
  updated: boolean;
  updatedPlugin: PluginDefinition;
  changeLog: RepairChangeLog;
}

const VARIABLE_DEFAULTS: Record<DocumentVariable, BlockOptionOutput> = {
  preceding_text: {
    source: "preceding_text",
    target: "preceding_text",
    minWords: 0,
    maxWords: 500,
  },
  highlighted_text: {
    source: "highlighted_text",
    target: "highlighted_text",
    minWords: 0,
    maxWords: 2000,
  },
  previous_document_text: {
    source: "previous_document_text",
    target: "previous_document_text",
    minWords: 0,
    maxWords: 2000,
  },
  documentId: {
    source: "documentId",
    target: "documentId",
  },
};

const deepClone = <T>(value: T): T => JSON.parse(JSON.stringify(value));

const normalizeVariable = (raw: unknown): string | undefined => {
  if (!raw || typeof raw !== "string") return undefined;
  const withoutFilter = raw.split("|")[0]?.trim();
  if (!withoutFilter) return undefined;
  return withoutFilter.split(".")[0];
};

const ensureArray = <T>(value?: T[] | null): T[] => {
  if (Array.isArray(value)) {
    return [...value];
  }
  return [];
};

const ensureOptions = (block: PluginBlock): void => {
  if (!block.options || typeof block.options !== "object") {
    block.options = {};
  }
  block.options.outputs = ensureArray(block.options.outputs);
};

const collectDocumentVariablesFromPrompts = (blocks: PluginBlock[] = []): Set<DocumentVariable> => {
  const used = new Set<DocumentVariable>();

  blocks
    .filter((block) => block.blockType === BLOCK_TYPES.INFERENCE)
    .forEach((block) => {
      const messages = block.options?.prompt?.messages;
      if (!Array.isArray(messages)) return;

      messages.forEach((message) => {
        if (!message?.template) return;
        const variables = getLiquidReferencedVariables(message.template) || [];
        variables.forEach((variable) => {
          const normalized = normalizeVariable(variable);
          if (normalized && isDocumentVariable(normalized)) {
            used.add(normalized);
          }
        });
      });
    });

  return used;
};

export const repairPluginDocumentVariables = (plugin: PluginDefinition | null | undefined): RepairResult => {
  if (!plugin) {
    return {
      updated: false,
      updatedPlugin: plugin as PluginDefinition,
      changeLog: { addedOptionOutputs: [], addedBlockOutputs: [], addedConnections: [], addedInputs: [], reason: "missing_plugin" },
    };
  }

  const updatedPlugin = deepClone(plugin);
  const blocks = ensureArray(updatedPlugin.blocks);
  const connections = ensureArray(updatedPlugin.connections);

  updatedPlugin.blocks = blocks;
  updatedPlugin.connections = connections;

  const inputBlock = blocks.find((block) => block.blockType === BLOCK_TYPES.INPUT_DOCUMENT);
  const inferenceBlocks = blocks.filter((block) => block.blockType === BLOCK_TYPES.INFERENCE);

  if (!inputBlock) {
    return {
      updated: false,
      updatedPlugin: plugin,
      changeLog: { addedOptionOutputs: [], addedBlockOutputs: [], addedConnections: [], addedInputs: [], reason: "missing_input_block" },
    };
  }

  ensureOptions(inputBlock);
  inputBlock.outputs = ensureArray(inputBlock.outputs);

  const usedVariables = collectDocumentVariablesFromPrompts(blocks);

  if (usedVariables.size === 0) {
    return {
      updated: false,
      updatedPlugin: plugin,
      changeLog: { addedOptionOutputs: [], addedBlockOutputs: [], addedConnections: [], addedInputs: [], reason: "no_document_variables_detected" },
    };
  }

  let didUpdate = false;
  const changeLog: RepairChangeLog = {
    addedOptionOutputs: [],
    addedBlockOutputs: [],
    addedConnections: [],
    addedInputs: [],
  };

  usedVariables.forEach((variable) => {
    const optionExists = inputBlock.options?.outputs?.some((output) => normalizeVariable(output.source) === variable);
    if (!optionExists) {
      const config = VARIABLE_DEFAULTS[variable] ?? { source: variable, target: variable };
      inputBlock.options?.outputs?.push({ ...config });
      changeLog.addedOptionOutputs.push(variable);
      didUpdate = true;
    }

    const blockOutputExists = inputBlock.outputs?.some((output) => output.name === variable);
    if (!blockOutputExists) {
      inputBlock.outputs?.push({ name: variable, type: variable === "documentId" ? "documentId" : "text" });
      changeLog.addedBlockOutputs.push(variable);
      didUpdate = true;
    }
  });

  inferenceBlocks.forEach((block) => {
    block.inputs = ensureArray(block.inputs);

    usedVariables.forEach((variable) => {
      const inputExists = block.inputs?.some((input) => input.name === variable);
      if (!inputExists) {
        block.inputs?.push({ name: variable, type: variable === "documentId" ? "documentId" : "text" });
        changeLog.addedInputs.push({ blockId: block.id, variable });
        didUpdate = true;
      }

      const connectionExists = connections.some((connection) => {
        if (connection.source !== inputBlock.id || connection.target !== block.id) return false;
        if (connection.targetPath !== variable) return false;
        return normalizeVariable(connection.sourcePath) === variable;
      });

      if (!connectionExists) {
        connections.push({
          source: inputBlock.id,
          sourcePath: variable,
          target: block.id,
          targetPath: variable,
        });
        changeLog.addedConnections.push({ blockId: block.id, variable });
        didUpdate = true;
      }
    });
  });

  if (!didUpdate) {
    return { updated: false, updatedPlugin: plugin, changeLog: { ...changeLog, reason: "already_up_to_date" } };
  }

  return { updated: true, updatedPlugin, changeLog };
};

export type SummarizeableChangeLog = Partial<RepairChangeLog>;

export const summarizeChangeLog = (changeLog: SummarizeableChangeLog = {}): string => {
  const { reason } = changeLog;

  if (reason) {
    switch (reason) {
      case "missing_input_block":
        return "Cannot repair: plugin has no input_document block.";
      case "no_document_variables_detected":
        return "No document variables were detected in the prompts.";
      case "already_up_to_date":
        return "No changes were necessary.";
      default:
        return "No changes were applied.";
    }
  }

  const parts: string[] = [];

  if (changeLog.addedOptionOutputs?.length) {
    parts.push(`added option outputs for ${changeLog.addedOptionOutputs.join(", ")}`);
  }
  if (changeLog.addedBlockOutputs?.length) {
    parts.push(`added block outputs for ${changeLog.addedBlockOutputs.join(", ")}`);
  }
  if (changeLog.addedConnections?.length) {
    const vars = [...new Set(changeLog.addedConnections.map((item) => item.variable))];
    parts.push(`created connections for ${vars.join(", ")}`);
  }
  if (changeLog.addedInputs?.length) {
    const vars = [...new Set(changeLog.addedInputs.map((item) => item.variable))];
    parts.push(`added inference inputs for ${vars.join(", ")}`);
  }

  if (parts.length === 0) {
    return "No changes were necessary.";
  }

  return parts.join("; ");
};
